import { NotificationSeverityIcon } from '~/features/notifications/components/notification-severity-icon';
import { NotificationTagBadge } from '~/features/notifications/components/notification-tag-badge';
import type { InboxNotification } from '~/features/notifications/types/notifications.api.types';
import { cn } from '~/utils/cn';
import { formatRelative } from '~/utils/date';

interface Props {
  notification: InboxNotification;
  onNavigate: (n: InboxNotification) => void;
}

/** Compact notification row for dropdown/panel lists. */
export function NotificationRow({ notification, onNavigate }: Props) {
  const {
    title,
    description,
    severity,
    type,
    icon,
    context,
    tags,
    is_read,
    created_at,
    has_action,
  } = notification;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open notification: ${title}${is_read ? '' : ', unread'}`}
      onClick={() => {
        onNavigate(notification);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onNavigate(notification);
      }}
      className={cn(
        'border-border w-full cursor-pointer border-b px-4 py-3 text-left last:border-0',
        'hover:bg-muted/50 transition-colors',
        !is_read && 'bg-blue-50/40',
      )}
    >
      <div className="flex gap-3">
        <div className="relative mt-0.5 shrink-0">
          <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-full">
            <NotificationSeverityIcon severity={severity} type={type} icon={icon} />
          </div>
          {!is_read && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-white"
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-foreground line-clamp-2 flex-1 text-sm leading-snug font-semibold">
              {title}
            </p>
            <span className="text-muted-foreground mt-0.5 shrink-0 text-[10px] whitespace-nowrap">
              {formatRelative(created_at)} · {context}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap gap-1">
            {tags.map((tag) => (
              <NotificationTagBadge key={tag} tag={tag} />
            ))}
          </div>

          {description ? (
            <p className="text-muted-foreground mt-1.5 line-clamp-2 text-xs">{description}</p>
          ) : null}

          {has_action ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(notification);
              }}
              className="border-border text-foreground hover:bg-muted mt-2 inline-flex cursor-pointer items-center rounded border px-2.5 py-1 text-xs font-medium transition-colors"
            >
              View More
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
